import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { FileDown, ArrowUpRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';

/**
 * Public catalog view — no auth required. Anyone with the slug URL can view.
 * Each product tile links to /c/:slug/p/:sku so buyers can drill into the full
 * product detail (description, specs, downloads) without needing an account.
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_IMG = `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 600'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='#FBF0F6'/><stop offset='1' stop-color='#C2478A'/></linearGradient></defs><rect width='600' height='600' fill='url(#g)'/><text x='300' y='320' text-anchor='middle' font-family='Georgia, serif' font-style='italic' font-size='140' fill='#0A0A0A' opacity='0.4'>IJE</text></svg>`)}`;

const resolveImg = (u) => {
  if (!u) return FALLBACK_IMG;
  if (u.startsWith('http') || u.startsWith('data:')) return u;
  if (u.startsWith('/api/')) return `${API_BASE}${u}`;
  return u;
};

// Card-grid variant — request `?thumb=1` for our own uploads so mobile
// listings pull the ~400px WebP instead of the full-size original.
const thumbImg = (u) => {
  if (!u) return FALLBACK_IMG;
  if (u.startsWith('/api/images/')) {
    const sep = u.includes('?') ? '&' : '?';
    return `${API_BASE}${u}${sep}thumb=1`;
  }
  return resolveImg(u);
};

const fmt = (n) => Number(n || 0).toFixed(2);

export default function PublicCatalog() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = new URLSearchParams(window.location.search).get('token') || '';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/catalogs/${slug}${token ? `?token=${encodeURIComponent(token)}` : ''}`);
        setData(data);
      } catch (e) {
        setErr(e.response?.status === 404 ? 'Catalog not found or private.' : 'Failed to load catalog.');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, token]);

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/public/catalogs/${slug}/pricelist.pdf${token ? `?token=${encodeURIComponent(token)}` : ''}`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'Catalog not available' : 'Could not download PDF');
    }
  };

  if (loading) return <div className="container-x py-20 sans text-neutral-500">Loading catalog…</div>;
  if (err) return <div className="container-x py-20 sans text-neutral-500">{err}</div>;

  const showBoth = data.pricing_mode === 'discounted' && data.discount_pct > 0;
  const isPrivatePreview = data.visibility && data.visibility !== 'public';
  const showSignupCta = !user || (user.role === 'buyer' && user.status !== 'active');

  return (
    <div className="container-x py-8 md:py-16" data-testid="public-catalog">
      {data.cover_image && (
        <div className="mb-8 md:mb-12 -mx-4 md:mx-0 md:rounded-sm overflow-hidden border-y md:border border-neutral-200 bg-neutral-100">
          <img
            src={resolveImg(data.cover_image)}
            alt=""
            className="w-full h-56 md:h-80 object-cover"
            referrerPolicy="no-referrer"
            data-testid="public-catalog-cover"
          />
        </div>
      )}

      {showSignupCta && (
        <section
          className="mb-10 md:mb-14 relative overflow-hidden border border-neutral-200 bg-gradient-to-br from-[color:var(--ije-pink-tint)] via-white to-white"
          data-testid="public-catalog-signup-cta"
        >
          <div className="relative p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <div className="flex-shrink-0 hidden md:flex w-12 h-12 rounded-full items-center justify-center" style={{ background: 'var(--ije-pink)' }}>
              <Sparkles size={22} className="text-white" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="eyebrow">Trade Access</p>
              <h2 className="h-display text-2xl md:text-3xl mt-1.5">Unlock pricing beyond wholesale.</h2>
              <p className="serif text-sm md:text-base text-neutral-700 mt-2 leading-relaxed">
                Approved retailers get account-level discounts, full catalog access, quote requests, and saved orders.
                Apply in under a minute or sign in if you already have an account.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-2 flex-shrink-0 md:min-w-[180px]">
              <Link
                to="/apply"
                className="btn-primary justify-center"
                data-testid="public-catalog-apply-cta"
              >
                Apply for account
              </Link>
              <Link
                to="/login"
                className="btn-outline justify-center"
                data-testid="public-catalog-login-cta"
              >
                Sign in
              </Link>
            </div>
          </div>
        </section>
      )}

      <header className="mb-8 md:mb-14">
        {isPrivatePreview && (
          <div
            className="mb-4 md:mb-6 border border-[color:var(--ije-pink)] bg-[color:var(--ije-pink-tint)] px-4 py-2.5 sans text-[11px] tracking-widest uppercase text-[color:var(--ije-pink-dark)]"
            data-testid="private-preview-banner"
          >
            Private preview — only you and admins can see this. Make it public to share.
          </div>
        )}
        <p className="eyebrow">Curated Catalog</p>
        <h1 className="h-display text-3xl md:text-5xl mt-3">{data.name}</h1>
        {data.description && (
          <p className="serif text-base md:text-lg text-neutral-700 mt-4 max-w-2xl leading-relaxed">
            {data.description}
          </p>
        )}
        <div className="mt-5 md:mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="sans text-xs uppercase tracking-widest text-neutral-500">
            {data.products.length} products
            {showBoth && <> · {data.discount_pct}% preview discount applied</>}
          </p>
          <button
            type="button"
            onClick={downloadPdf}
            className="btn-primary w-full md:w-auto justify-center"
            data-testid="public-catalog-download-pdf"
          >
            <FileDown size={16} /> Download Price List (PDF)
          </button>
        </div>
      </header>

      {data.products.length === 0 ? (
        <p className="serif text-xl italic text-neutral-500">This catalog is empty.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {data.products.map((p) => (
            <Link
              to={`/c/${slug}/p/${encodeURIComponent(p.sku)}`}
              key={p.sku}
              className="product-card group focus:outline-none focus:ring-2 focus:ring-[color:var(--ije-pink)]"
              data-testid={`public-card-${p.sku}`}
            >
              <div className="product-card__image relative">
                <img src={thumbImg((p.images || [])[0])} alt={p.name} loading="lazy" referrerPolicy="no-referrer" />
                <span
                  className="absolute top-2 right-2 bg-black/70 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity md:flex hidden"
                  aria-hidden
                >
                  <ArrowUpRight size={14} />
                </span>
              </div>
              <div className="p-3 md:p-5 flex flex-col gap-1.5 md:gap-2">
                <span className="eyebrow truncate">{p.brand}</span>
                <h3 className="serif text-sm md:text-lg leading-snug line-clamp-2">{p.name}</h3>
                <p className="sans text-[10px] md:text-xs text-neutral-500 tracking-widest uppercase truncate">{p.sku}</p>
                <div className="mt-auto pt-2 md:pt-3 space-y-0.5">
                  <p className="sans text-[10px] md:text-xs text-neutral-500 tracking-widest uppercase">
                    MSRP <span className="text-neutral-700">${fmt(p.msrp)}</span>
                  </p>
                  <p className="sans text-xs md:text-sm">
                    <span className="text-neutral-500 tracking-widest uppercase text-[10px] md:text-xs mr-1.5 md:mr-2">Wholesale</span>
                    {showBoth ? (
                      <>
                        <span className="line-through text-neutral-500">${fmt(p.wholesale_price)}</span>
                        <span className="serif text-base md:text-lg pink-gradient-text ml-1.5 md:ml-2">${fmt(p.display_price)}</span>
                      </>
                    ) : (
                      <span className="serif text-base md:text-lg">${fmt(p.wholesale_price)}</span>
                    )}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <footer className="mt-16 pt-8 border-t border-neutral-200 sans text-xs uppercase tracking-widest text-neutral-500 text-center md:text-left">
        IJE HAUS Wholesale · priscila@ijehaus.com · Contact us to place an order
      </footer>
    </div>
  );
}
